import React, { useState } from 'react';
import { Pencil, X, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useUserData } from '../context/UserDataContext';
import { appendHistorySnapshot } from '../lib/userData';
import EmptyState from './EmptyState';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeRating(score) {
  if (score >= 800) return 'Exceptional';
  if (score >= 740) return 'Very Good';
  if (score >= 670) return 'Good';
  if (score >= 580) return 'Fair';
  return 'Poor';
}

// ─── SVG arc gauge ────────────────────────────────────────────────────────────

function ArcGauge({ score }) {
  const min = 300, max = 850;
  const pct = (score - min) / (max - min);

  const toRad = (d) => (d * Math.PI) / 180;
  const cx = 100, cy = 90, r = 72;

  const startAngle = -150;
  const endAngle = startAngle + pct * 300;

  const sx = cx + r * Math.cos(toRad(startAngle));
  const sy = cy + r * Math.sin(toRad(startAngle));
  const ex = cx + r * Math.cos(toRad(endAngle));
  const ey = cy + r * Math.sin(toRad(endAngle));

  const fullEx = cx + r * Math.cos(toRad(150));
  const fullEy = cy + r * Math.sin(toRad(150));

  const largeArc = pct * 300 > 180 ? 1 : 0;

  const scoreColor =
    score >= 800 ? '#10b981'
    : score >= 740 ? '#3b82f6'
    : score >= 670 ? '#f59e0b'
    : '#ef4444';

  const rating = computeRating(score);

  return (
    <div className="flex flex-col items-center">
      <svg width="200" height="130" viewBox="0 0 200 130">
        {/* Background arc */}
        <path
          d={`M ${cx + r * Math.cos(toRad(-150))} ${cy + r * Math.sin(toRad(-150))} A ${r} ${r} 0 1 1 ${fullEx} ${fullEy}`}
          fill="none"
          stroke="#334155"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* Filled arc */}
        {pct > 0 && (
          <path
            d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
            fill="none"
            stroke={scoreColor}
            strokeWidth="12"
            strokeLinecap="round"
          />
        )}
        {/* Glow effect */}
        <path
          d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
          fill="none"
          stroke={scoreColor}
          strokeWidth="20"
          strokeLinecap="round"
          opacity="0.15"
        />
        {/* Score text */}
        <text x={cx} y={cy - 5} textAnchor="middle" fill={scoreColor} fontSize="32" fontWeight="900" fontFamily="system-ui">
          {score}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" fill="#94a3b8" fontSize="12" fontFamily="system-ui">
          {rating}
        </text>
        {/* Range labels */}
        <text x="20" y="118" fill="#475569" fontSize="10" fontFamily="system-ui">300</text>
        <text x="168" y="118" fill="#475569" fontSize="10" fontFamily="system-ui">850</text>
      </svg>
    </div>
  );
}

// ─── Score Modal ──────────────────────────────────────────────────────────────

function ScoreModal({ score, setScore, date, setDate, onSave, onClose, isUpdate }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-100">
            {isUpdate ? 'Update Score' : 'Log Credit Score'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Score slider + number input */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Credit Score
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={300}
              max={850}
              value={score}
              onChange={(e) => setScore(Number(e.target.value))}
              className="flex-1 accent-blue-500"
            />
            <input
              type="number"
              min={300}
              max={850}
              value={score}
              onChange={(e) => {
                const v = Math.min(850, Math.max(300, Number(e.target.value)));
                setScore(v);
              }}
              className="w-20 bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 text-slate-100 text-sm text-center focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="mt-2 text-xs text-slate-400">
            Rating: <span className="text-slate-200 font-medium">{computeRating(score)}</span>
          </div>
        </div>

        {/* Date input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Submit */}
        <button
          onClick={onSave}
          disabled={!date}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
        >
          {isUpdate ? 'Update Score' : 'Log Score'}
        </button>
      </div>
    </div>
  );
}

// ─── Status colours ───────────────────────────────────────────────────────────

const STATUS_COLORS = {
  excellent: 'text-emerald-400 bg-emerald-900/40 border-emerald-700/40',
  good:      'text-blue-400 bg-blue-900/40 border-blue-700/40',
  fair:      'text-amber-400 bg-amber-900/40 border-amber-700/40',
  poor:      'text-rose-400 bg-rose-900/40 border-rose-700/40',
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function CreditScore() {
  const { creditData, updateSection } = useUserData();

  const [showModal, setShowModal] = useState(false);
  const [modalScore, setModalScore] = useState(700);
  const [modalDate,  setModalDate]  = useState(() => new Date().toISOString().slice(0, 10));

  function openModal() {
    setModalScore(creditData ? creditData.score : 700);
    setModalDate(new Date().toISOString().slice(0, 10));
    setShowModal(true);
  }

  function handleSave() {
    if (!modalDate) return;

    if (!creditData) {
      // First-time log — build a brand-new credit record
      const monthAbbr = new Date(modalDate).toLocaleDateString('en-US', { month: 'short' });
      const newData = {
        score:       modalScore,
        rating:      computeRating(modalScore),
        lastUpdated: modalDate,
        change:      0,
        factors:     [],
        history:     [{ month: monthAbbr, score: modalScore }],
      };
      updateSection('credit', newData);
    } else {
      // Update existing record — append history snapshot and compute change
      const rawHistory = appendHistorySnapshot(
        // appendHistorySnapshot uses { month, value } internally; adapt to { month, score }
        creditData.history.map(h => ({ month: h.month, value: h.score })),
        modalScore,
      );
      const newHistory = rawHistory.map(h => ({
        month: h.month,
        score: h.value !== undefined ? h.value : h.score,
      }));

      const updatedData = {
        ...creditData,
        score:       modalScore,
        rating:      computeRating(modalScore),
        lastUpdated: modalDate,
        change:      modalScore - creditData.score,
        history:     newHistory,
      };
      updateSection('credit', updatedData);
    }

    setShowModal(false);
  }

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!creditData) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Credit Score</h1>
          <p className="text-slate-400 text-sm mt-0.5">Track your credit health over time.</p>
        </div>

        <EmptyState
          icon="💳"
          title="No credit score logged yet"
          description="Add your current credit score to track it over time."
          actionLabel="Log My Score"
          onAction={() => setShowModal(true)}
        />

        {showModal && (
          <ScoreModal
            score={modalScore}
            setScore={setModalScore}
            date={modalDate}
            setDate={setModalDate}
            onSave={handleSave}
            onClose={() => setShowModal(false)}
            isUpdate={false}
          />
        )}
      </div>
    );
  }

  // ── Full dashboard ──────────────────────────────────────────────────────────
  const changePositive = creditData.change >= 0;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Credit Score</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Last updated {new Date(creditData.lastUpdated).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg text-slate-300 text-sm font-medium transition-colors"
        >
          <Pencil size={14} />
          Update Score
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gauge */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 flex flex-col items-center">
          <ArcGauge score={creditData.score} />

          <div className="flex items-center gap-2 mt-2">
            <TrendingUp size={14} className={changePositive ? 'text-emerald-400' : 'text-rose-400'} />
            <span className={`text-sm font-medium ${changePositive ? 'text-emerald-400' : 'text-rose-400'}`}>
              {changePositive ? '+' : ''}{creditData.change} points this month
            </span>
          </div>

          {/* Score bands */}
          <div className="w-full mt-5 space-y-1.5">
            {[
              { label: 'Exceptional', range: '800–850', active: creditData.score >= 800 },
              { label: 'Very Good',   range: '740–799', active: creditData.score >= 740 && creditData.score < 800 },
              { label: 'Good',        range: '670–739', active: creditData.score >= 670 && creditData.score < 740 },
              { label: 'Fair',        range: '580–669', active: creditData.score >= 580 && creditData.score < 670 },
              { label: 'Poor',        range: '300–579', active: creditData.score < 580 },
            ].map(band => (
              <div key={band.label} className={`flex justify-between px-3 py-1.5 rounded-lg text-xs ${
                band.active ? 'bg-blue-900/40 border border-blue-700/40 text-blue-300 font-medium' : 'text-slate-500'
              }`}>
                <span>{band.label}</span>
                <span>{band.range}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Score history chart */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-base mb-4">6-Month History</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={creditData.history} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis domain={['auto', 'auto']} tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                labelStyle={{ color: '#cbd5e1' }}
                formatter={(v) => [v, 'Score']}
              />
              <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2.5} dot={{ fill: '#3b82f6', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>

          <div className="mt-4 space-y-2">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">What moved your score</div>
            <div className="p-3 bg-emerald-950/40 border border-emerald-700/30 rounded-lg text-xs text-emerald-300">
              ✓ On-time payment streak maintained · Credit utilization dropped 3%
            </div>
          </div>
        </div>
      </div>

      {/* Factors — only rendered when data is present */}
      {creditData.factors.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h2 className="font-semibold text-slate-200 text-base mb-4">Score Factor Breakdown</h2>
          <div className="space-y-3">
            {creditData.factors.map(f => (
              <div key={f.name} className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-300 font-medium">{f.name}</span>
                      <span className="text-xs text-slate-600">{f.weight}% of score</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${STATUS_COLORS[f.status]}`}>
                      {f.status}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${
                        f.status === 'excellent' ? 'bg-emerald-500'
                        : f.status === 'good' ? 'bg-blue-500'
                        : f.status === 'fair' ? 'bg-amber-500' : 'bg-rose-500'
                      }`}
                      style={{ width: `${f.status === 'excellent' ? 95 : f.status === 'good' ? 75 : 50}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{f.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h2 className="font-semibold text-slate-200 text-base mb-4">How to Improve</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { tip: 'Keep utilization below 10%', detail: 'You\'re at 18% — pay down an extra $200 before statement close to drop to ~10%', impact: 'Medium' },
            { tip: 'Don\'t open new accounts', detail: 'Each application causes a hard inquiry. You have 1 — wait until this falls off in 12 months', impact: 'Low' },
            { tip: 'Keep old accounts open', detail: 'Closing cards shortens your average credit age. Keep that old card with no annual fee open.', impact: 'Medium' },
          ].map(t => (
            <div key={t.tip} className="p-4 bg-slate-700/40 rounded-xl">
              <div className="text-xs text-blue-400 font-medium mb-1">Impact: {t.impact}</div>
              <div className="text-sm font-semibold text-slate-200 mb-2">{t.tip}</div>
              <div className="text-xs text-slate-400 leading-relaxed">{t.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Update modal */}
      {showModal && (
        <ScoreModal
          score={modalScore}
          setScore={setModalScore}
          date={modalDate}
          setDate={setModalDate}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          isUpdate={true}
        />
      )}
    </div>
  );
}
